import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Bot, Search, Target, Save, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const WINDOWS = [28, 90, 180, 365];

const Stat = ({ label, value, hint, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-navy mt-1">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const Section = ({ icon: Icon, title, children, note, testid }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <h3 className="font-bold text-navy flex items-center gap-2"><Icon size={18} className="text-orange" /> {title}</h3>
    {note && <p className="text-xs text-slate-500 mt-1">{note}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

function LogForm({ months, questions, platforms, onSaved }) {
  const [form, setForm] = useState({
    month: months.this_month, platform: platforms[0], question_id: questions[0].id,
    appeared: false, recommended: false, cited: false, cited_url: "", accurate: false,
    visit_followed: false, response_text: "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/aeo/recommendation-log", form);
      toast.success("Logged.");
      setForm((f) => ({ ...f, response_text: "", notes: "", cited_url: "" }));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that entry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="aeo-log-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Month</label>
          <Input value={form.month} onChange={(e) => set("month", e.target.value)} placeholder="2026-06" data-testid="aeo-log-month" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Platform</label>
          <select value={form.platform} onChange={(e) => set("platform", e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-md px-2 py-2" data-testid="aeo-log-platform">
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Question</label>
          <select value={form.question_id} onChange={(e) => set("question_id", e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-md px-2 py-2" data-testid="aeo-log-question">
            {questions.map((q) => <option key={q.id} value={q.id}>{q.text}</option>)}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap gap-4">
        {[["appeared", "CASS appeared"], ["recommended", "CASS was recommended"], ["cited", "Cited with a link"],
          ["accurate", "What it said was accurate"], ["visit_followed", "A visit followed"]].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`aeo-log-${k}`} />
            {label}
          </label>
        ))}
      </div>
      <Input value={form.cited_url} onChange={(e) => set("cited_url", e.target.value)}
             placeholder="The URL it linked to, if any" data-testid="aeo-log-url" />
      <Textarea rows={4} value={form.response_text} onChange={(e) => set("response_text", e.target.value)}
                placeholder="Paste the response you were given — kept as evidence" data-testid="aeo-log-response" />
      <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes" data-testid="aeo-log-notes" />
      <Button onClick={save} disabled={saving} data-testid="aeo-log-save">
        {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />} Log this answer
      </Button>
    </div>
  );
}

export default function AeoDashboard() {
  const [days, setDays] = useState(90);
  const [referrals, setReferrals] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [ga4, setGa4] = useState(null);
  const [branded, setBranded] = useState(null);
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [ref, conv, g, b, l] = await Promise.all([
      api.get("/admin/aeo/ai-referrals", { params: { days } }).then((r) => r.data).catch(() => null),
      api.get("/admin/aeo/conversions", { params: { days } }).then((r) => r.data).catch(() => null),
      api.get("/admin/ga4/status").then((r) => r.data).catch(() => null),
      api.get("/admin/gsc/branded-split", { params: { days: days === 90 ? 90 : days === 28 ? 28 : 90 } })
        .then((r) => r.data).catch((e) => ({ error: e?.response?.data?.detail || "Search Console isn't connected yet." })),
      api.get("/admin/aeo/recommendation-log").then((r) => r.data).catch(() => null),
    ]);
    setReferrals(ref); setConversions(conv); setGa4(g); setBranded(b); setLog(l);
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const deleteEntry = async (id) => {
    await api.delete("/admin/aeo/recommendation-log", { params: { id } });
    load();
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500" data-testid="aeo-loading">
      <Loader2 className="animate-spin" size={16} /> Loading…
    </div>;
  }

  const c = conversions || {};
  const labels = c.labels || {};

  return (
    <div className="space-y-6" data-testid="aeo-dashboard">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Period</span>
        {WINDOWS.map((w) => (
          <button key={w} onClick={() => setDays(w)} data-testid={`aeo-period-${w}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${days === w ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>
            {w} days
          </button>
        ))}
      </div>

      <Section icon={Search} title="Traditional search" testid="aeo-search"
               note={branded?.note || "From Search Console, compared with the previous period of the same length."}>
        {branded?.error ? (
          <p className="text-sm text-amber-700" data-testid="aeo-gsc-missing">{branded.error}</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Non-branded clicks" value={branded.current.non_branded.clicks}
                    hint={`${branded.previous.non_branded.clicks} in the previous period`} testid="aeo-nonbranded-clicks" />
              <Stat label="Non-branded impressions" value={branded.current.non_branded.impressions}
                    hint={`${branded.previous.non_branded.impressions} before`} testid="aeo-nonbranded-impressions" />
              <Stat label="Branded clicks" value={branded.current.branded.clicks}
                    hint={`${branded.previous.branded.clicks} before`} testid="aeo-branded-clicks" />
              <Stat label="Non-branded CTR" value={`${(branded.current.non_branded.ctr * 100).toFixed(1)}%`} testid="aeo-nonbranded-ctr" />
            </div>
            {(branded.top_non_branded || []).length > 0 && (
              <table className="min-w-full text-sm mt-4" data-testid="aeo-nonbranded-table">
                <thead className="text-slate-600"><tr>
                  <th className="text-left py-1">Non-branded search</th><th className="text-left py-1">Impressions</th>
                  <th className="text-left py-1">Clicks</th><th className="text-left py-1">Avg position</th>
                </tr></thead>
                <tbody>
                  {branded.top_non_branded.map((r) => (
                    <tr key={r.query} className="border-t border-slate-100">
                      <td className="py-1 pr-4">{r.query}</td><td className="py-1">{r.impressions}</td>
                      <td className="py-1">{r.clicks}</td><td className="py-1">{r.position}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Section>

      <Section icon={Bot} title="AI referrals" testid="aeo-referrals" note={referrals?.caveat}>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Enquiries in the period" value={referrals?.enquiries_in_window ?? 0} testid="aeo-enquiries" />
          <Stat label="Attributable to AI platforms" value={referrals?.ai_attributed ?? 0}
                hint="Referrer recorded on the visit" testid="aeo-ai-attributed" />
          <Stat label="Platforms seen" value={(referrals?.by_platform || []).length} testid="aeo-platforms" />
        </div>
        {(referrals?.by_platform || []).length === 0 ? (
          <p className="text-sm text-slate-600 mt-4" data-testid="aeo-referrals-empty">
            No enquiry in this period arrived with an AI platform as its referrer. That is a measured zero for referred
            enquiries, not proof that no one asked an assistant about CASS.
          </p>
        ) : (
          <table className="min-w-full text-sm mt-4" data-testid="aeo-referrals-table">
            <thead className="text-slate-600"><tr>
              <th className="text-left py-1">Platform</th><th className="text-left py-1">Enquiries</th>
              <th className="text-left py-1">Previous period</th><th className="text-left py-1">Top landing pages</th>
            </tr></thead>
            <tbody>
              {referrals.by_platform.map((p) => (
                <tr key={p.platform} className="border-t border-slate-100 align-top">
                  <td className="py-1 pr-4 font-medium">{p.platform}</td>
                  <td className="py-1">{p.total}</td>
                  <td className="py-1">{p.previous}</td>
                  <td className="py-1 text-slate-600">{(p.landing_pages || []).map((l) => `${l.path} (${l.count})`).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-slate-500 mt-3">
          {ga4?.connected
            ? "GA4 is connected, so session-level AI traffic is available as well."
            : "Session and landing-page figures for AI traffic need the GA4 Data API, which isn't connected yet."}
        </p>
        {!ga4?.connected && (
          <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="aeo-ga4-missing">
            <strong>GA4 not connected.</strong> To switch session reporting on, the following are needed:
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              {(ga4?.needs || []).map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
        )}
      </Section>

      <Section icon={Target} title="Conversions" testid="aeo-conversions"
               note={c.note}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {Object.entries(labels).map(([k, label]) => (
            <Stat key={k} label={label} value={(c.totals || {})[k] || 0}
                  hint={`${(c.previous_totals || {})[k] || 0} in the previous period`} testid={`aeo-conv-${k}`} />
          ))}
        </div>
        {(c.by_source || []).length > 0 && (
          <table className="min-w-full text-sm mt-4" data-testid="aeo-conversions-table">
            <thead className="text-slate-600"><tr>
              <th className="text-left py-1">Where they came from</th><th className="text-left py-1">Total</th>
              <th className="text-left py-1">Contact</th><th className="text-left py-1">Pricing</th>
              <th className="text-left py-1">Downloads</th><th className="text-left py-1">Calls</th>
            </tr></thead>
            <tbody>
              {c.by_source.map((r) => (
                <tr key={r.source} className="border-t border-slate-100">
                  <td className="py-1 pr-4">{r.source}</td><td className="py-1">{r.total}</td>
                  <td className="py-1">{r.contact || 0}</td><td className="py-1">{r.pricing || 0}</td>
                  <td className="py-1">{r.download || 0}</td><td className="py-1">{r.booking || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section icon={Bot} title="AI recommendation monitoring" testid="aeo-log"
               note="A fixed question set, asked by hand once a month on each platform. The response is kept as evidence; nothing here is estimated.">
        {log && (
          <>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Stat label="Answers logged" value={log.summary.logged} testid="aeo-log-count" />
              <Stat label="Appeared" value={log.summary.appeared} testid="aeo-log-appeared" />
              <Stat label="Recommended" value={log.summary.recommended} testid="aeo-log-recommended" />
              <Stat label="Cited with a link" value={log.summary.cited} testid="aeo-log-cited" />
              <Stat label="Accurate" value={log.summary.accurate} testid="aeo-log-accurate" />
              <Stat label="Visit followed" value={log.summary.visit_followed} testid="aeo-log-visits" />
            </div>

            {log.outstanding.length > 0 && (
              <p className="text-xs text-amber-700 mt-3" data-testid="aeo-log-outstanding">
                {log.outstanding.length} of {log.platforms.length * log.questions.length} checks still to do for {log.this_month}.
              </p>
            )}

            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <LogForm months={{ this_month: log.this_month }} questions={log.questions}
                       platforms={log.platforms} onSaved={load} />
            </div>

            {log.entries.length > 0 && (
              <div className="overflow-x-auto mt-5">
                <table className="min-w-full text-sm" data-testid="aeo-log-table">
                  <thead className="text-slate-600"><tr>
                    <th className="text-left py-1">Month</th><th className="text-left py-1">Platform</th>
                    <th className="text-left py-1">Question</th><th className="text-left py-1">Result</th>
                    <th className="text-left py-1">Link given</th><th className="py-1"></th>
                  </tr></thead>
                  <tbody>
                    {log.entries.map((e) => (
                      <tr key={e.id} className="border-t border-slate-100 align-top">
                        <td className="py-1 pr-3">{e.month}</td>
                        <td className="py-1 pr-3">{e.platform}</td>
                        <td className="py-1 pr-3 text-slate-600">{e.question}</td>
                        <td className="py-1 pr-3">
                          {[e.appeared && "appeared", e.recommended && "recommended", e.cited && "cited",
                            e.accurate && "accurate", e.visit_followed && "visit followed"]
                            .filter(Boolean).join(", ") || "not mentioned"}
                        </td>
                        <td className="py-1 pr-3 text-slate-600">{e.cited_url || "—"}</td>
                        <td className="py-1 text-right">
                          <Button variant="outline" size="sm" onClick={() => deleteEntry(e.id)}
                                  data-testid={`aeo-log-delete-${e.id}`}><Trash2 size={14} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
